import React from 'react';

/**
 * EmptyState — 자료 부족 / 빈 섹션 표시.
 *
 * 와이어프레임 detail-sparse.html의 정직한 미확인 처리 패턴.
 * 일러스트 + 카피 + "알림 받기" CTA.
 *
 * variant:
 *   - banner: 페이지 상단 진행 안내 (주황 배경)
 *   - section: 섹션 내부 빈 영역 (중앙 정렬)
 */

export type EmptyStateVariant = 'banner' | 'section';

export interface EmptyStateProps {
  variant?: EmptyStateVariant;
  title: string;
  description?: string;
  /** "알림 받기" 등 보조 CTA */
  actionLabel?: string;
  onAction?: () => void;
  /** SVG 일러스트 노드 */
  illustration?: React.ReactNode;
  className?: string;
}

const InfoIcon: React.FC = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.2} strokeLinecap="round" strokeLinejoin="round" aria-hidden>
    <circle cx="12" cy="12" r="9" />
    <path d="M12 8v.01M11 12h1v5h1" />
  </svg>
);

export const EmptyState: React.FC<EmptyStateProps> = ({
  variant = 'section',
  title,
  description,
  actionLabel,
  onAction,
  illustration,
  className = '',
}) => {
  if (variant === 'banner') {
    return (
      <div
        role="status"
        className={`mx-4 my-3 rounded-ca-xl border border-[#F0DDB0] bg-accent-50 px-3.5 py-3 text-[12.5px] leading-relaxed text-[#6B4A14] ${className}`}
      >
        <div className="flex gap-2">
          <span className="mt-0.5 inline-flex h-4 w-4 shrink-0 text-[#B68B22]"><InfoIcon /></span>
          <div className="flex-1">
            <b className="font-bold text-[#3F2C00]">{title}</b>
            {description && <p className="mt-0.5 text-[12px] text-[#6B4A14]">{description}</p>}
          </div>
          {actionLabel && onAction && (
            <button
              type="button"
              onClick={onAction}
              className="shrink-0 self-start rounded-ca-lg bg-frame border border-[#F0DDB0] px-3 py-1.5 text-[11.5px] font-bold text-[#6B4A14]"
            >
              {actionLabel}
            </button>
          )}
        </div>
      </div>
    );
  }

  // section
  return (
    <div className={`flex flex-col items-center justify-center px-4 py-8 text-center ${className}`}>
      {illustration && <div className="mb-3 text-mute2">{illustration}</div>}
      <h3 className="text-[14.5px] font-extrabold text-ink tracking-tight">{title}</h3>
      {description && (
        <p className="mt-1.5 max-w-[280px] text-[12.5px] leading-relaxed text-muted">{description}</p>
      )}
      {actionLabel && onAction && (
        <button
          type="button"
          onClick={onAction}
          className="mt-4 inline-flex items-center gap-1.5 rounded-ca-xl border border-ca-line-strong bg-frame px-4 py-2.5 text-[12.5px] font-bold text-ink active:translate-y-[1px]"
        >
          {actionLabel}
        </button>
      )}
    </div>
  );
};

export default EmptyState;
